import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    School as SchoolIcon,
    LocationCity as LocationCityIcon,
    AttachMoney as AttachMoneyIcon,
    People as PeopleIcon,
    CheckCircle as CheckCircleIcon,
    Work as WorkIcon,
    MenuBook as MenuBookIcon,
    EmojiEvents as EmojiEventsIcon,
    Chat as ChatIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアで広がるキャンパスライフ' },
    { id: 'area', label: '大学別おすすめエリア' },
    { id: 'cheap', label: '学生向け格安ルームシェア物件の選び方' },
    { id: 'balance', label: '勉強とプライベートの両立術' },
    { id: 'circle', label: 'サークル・バイトとルームシェアの相性' },
    { id: 'experience', label: 'リアルな体験談とアドバイス' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで広がるキャンパスライフ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    新生活を迎える大学生のみなさん、「せっかくのキャンパスライフ、もっと充実させたい！」と感じていませんか？<b>ルームシェア</b>は、住まいのコストを抑えつつ、自分らしい暮らしや友情、経験を手に入れる現代の学生にぴったりの選択肢です。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    大学ごとに異なるおすすめエリア、学生向け格安物件の選び方、勉強・サークル・バイトとの両立ポイント、そして実際の体験談まで、ルームシェア専門家の視点で徹底解説します。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/roommates-living_student-roomshare-campuslife.jpg"
                        alt="明るい共有リビングで、さまざまな大学生がリラックスしながら会話や勉強をしている。大きな窓から自然光が差し込み、カラフルなインテリアと観葉植物が並ぶ和やかな雰囲気。"
                        style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 8px #ddd' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" />
                    <Chip label="学生生活" color="primary" variant="outlined" />
                    <Chip label="賃貸" color="primary" variant="outlined" />
                    <Chip label="節約" color="primary" variant="outlined" />
                </Box>
            </Box>

            {/* 大学別おすすめエリア */}
            <Box id="area" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <LocationCityIcon sx={{ mr: 1, color: 'primary.main' }} />
                    大学別おすすめエリア
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    首都圏や関西圏など、人気大学周辺ではルームシェアの選択肢も豊富です。大学の立地や通学の利便性、生活コスト、治安、周辺環境などを総合的に考慮しましょう。
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SchoolIcon sx={{ color: 'secondary.main', mr: 1 }} /> 早稲田・高田馬場エリア
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="家賃相場がやや高めだが、シェアなら割安に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="学生向けの飲食店・スーパーが充実" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="複数路線利用で通学も便利" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SchoolIcon sx={{ color: 'secondary.main', mr: 1 }} /> 京大・同志社・関大周辺
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="下宿文化が根付くが、シェアならさらにコスパ良し" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="鴨川沿いの落ち着いた環境が人気" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="市内各地へのアクセスも抜群" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SchoolIcon sx={{ color: 'secondary.main', mr: 1 }} /> 明治・青山・上智エリア
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="都心へのアクセス良好、遊びも勉強も両立" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="駅近物件はシェアで探すと格安になることも" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SchoolIcon sx={{ color: 'secondary.main', mr: 1 }} /> 地方国公立大周辺
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="家賃相場が低めで、広い物件も狙える" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="自然環境に恵まれ、のびのび暮らせる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>

                </Box>
            </Box>

            {/* 学生向け格安ルームシェア物件の選び方 */}
            <Box id="cheap" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AttachMoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                    学生向け格安ルームシェア物件の選び方
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    お財布にやさしいルームシェア選びは、ポイントを押さえて探すのがコツ。家賃だけでなく、初期費用や共益費、光熱費、ネット代などトータルコストを確認しましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                格安物件チェックリスト
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="家賃・共益費・光熱費込みで月3〜5万円が目安" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="敷金・礼金ゼロ物件を優先" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="最低限の家具・家電付きで初期コスト減" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="シェアメイトの属性（年齢・学部など）もポイント" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                選ぶ際のポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="通学・バイト先へのアクセスと家賃のバランス" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="事前に内見し、共用部の清潔感や雰囲気を確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoneyIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="ルームシェアルールを細かくチェック" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>

                </Box>
            </Box>

            {/* 勉強とプライベートの両立術 */}
            <Box id="balance" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                    勉強とプライベートの両立術
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア最大の魅力は「一人暮らし以上の充実感」。でも、勉強やプライベート時間の確保も大事です。両立のコツをまとめました。
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="個室の有無を重視"
                                secondary="自室で集中できる環境が理想。個室が難しい場合は、図書館やカフェも活用しましょう。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="共用部の使い方ルールを決める"
                                secondary="勉強時間や静かにしたい時間帯は事前にシェアメイトと話し合っておくとトラブル防止に。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="スケジュール共有でストレスフリー"
                                secondary="GoogleカレンダーやLINEグループで予定を共有し、生活リズムの違いを調整しましょう。"
                            />
                        </ListItem>
                    </List>
                </Box>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        専門家のアドバイス
                    </Typography>
                    <Typography variant="body2">
                        ルームシェアは「相手を思いやる」ことが最大の秘訣です。困った時・集中したい時は素直に伝え、オンオフの切り替えを意識しましょう。定期的な「お茶会」や「ルール見直し会」もおすすめ！
                    </Typography>
                </Paper>
            </Box>

            {/* サークル・バイトとルームシェアの相性 */}
            <Box id="circle" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                    サークル・バイトとルームシェアの相性
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    サークル活動やアルバイト、インターンなど、学生生活はとにかく多忙。ルームシェアだからこそ得られるメリットもたくさんあります。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <List>
                                <ListItem>
                                    <ListItemIcon><EmojiEventsIcon color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="サークル仲間と住むと、練習やイベントの準備がスムーズ"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WorkIcon color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="バイト・インターンの情報交換やシフト調整もサポートし合える"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleIcon color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="一緒に過ごす時間が増え、信頼関係が深まる"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <List>
                                <ListItem>
                                    <ListItemIcon><ChatIcon color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="忙しい時は助け合い、家事やゴミ出しも分担で効率UP"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="家に帰れば仲間がいる安心感・孤独感ゼロ"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="異なるサークル・バイトのメンバーと住むことで、視野や人脈も広がる"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 体験談 */}
            <Box id="experience" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    リアルな体験談とアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3 }}>
                    <Card sx={{ mb: 2, borderLeft: '5px solid #1976d2', borderRadius: 2, bgcolor: '#f3f6f9' }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Case 1：勉強も遊びも充実！都内シェアで得た自立心
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                「早稲田の近くで3人暮らし。最初は生活リズムの違いに戸惑いましたが、週1回の“ルームミーティング”でお互いの予定や困っていることを話し合うように。バイトやサークルの情報交換もできて、今では全員が自分の時間も大切にしつつ、イベントやご飯会も楽しんでいます！」（2年・女性）
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ mb: 2, borderLeft: '5px solid #388e3c', borderRadius: 2, bgcolor: '#f3f6f9' }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Case 2：地方国立大×広い一軒家でのびのび生活
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                「地方の大学で築古の一軒家を4人でシェア。家賃はひとり2.5万円、庭でBBQしたり、リビングで合同勉強会も。時にはケンカもありますが、悩みを共有できる仲間がいるから乗り越えられました。卒業後も一生の友人です！」（4年・男性）
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/roommates-dinner_student-roomshare-campuslife.jpg"
                        alt="夕方のダイニングテーブルで、大学生ルームメイトたちが手作りの夕食を囲みながら笑顔で談笑している。温かい照明と木目調インテリアが居心地の良さを演出"
                        style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 8px #ddd' }}
                    />
                </Box>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        まとめ 〜 ルームシェアで得られる最高の“学び”と“出会い”
                    </Typography>
                    <Typography variant="body2">
                        ルームシェアは、節約や利便性だけでなく、<b>多様な価値観や人間関係、社会性</b>を学ぶ貴重な機会。自分に合った物件・仲間を見つけて、最高のキャンパスライフをスタートしましょう！
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'student-roomshare-campuslife',
    slug: 'student-roomshare-campuslife',
    title: '学生必見！キャンパスライフを充実させるルームシェアの活用法',
    excerpt: '大学別おすすめエリア 学生向け格安物件 勉強とプライベート両立 サークル・バイト両立 体験談',
    coverImage: '/images/blog/cover-student-roomshare-campuslife_student-roomshare-campuslife.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な大学生たちがリラックスしながら会話や食事を楽しみ、インテリアはナチュラルで温かみがあり、学生生活の充実感と交流の雰囲気が伝わる',
    publishedAt: '2024-06-09T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '学生生活', '賃貸・住まい'],
    tags: ['ルームシェア', '学生賃貸', '格安物件', '大学エリア', '両立'],
    author: {
        name: '中村 彩乃',
        avatar: '/images/avatars/ayano-nakamura.jpg',
        bio: '大学専門のルームシェアサポート歴10年以上。学生向け住まい選びと人間関係構築のアドバイザーとして、首都圏・関西圏を中心に数百件の相談実績。趣味はカフェ巡りと読書。',
        profilePicture: '/images/avatars/ayano-nakamura.jpg'
    },
    viewCount: 1872,
    contentComponent: PostContent
};

export default post;